import type { WorkoutOverride } from '@/domain/health/workoutAppearance'
import type { IntakeRecord, SavedFood } from '@/domain/health/nutrition'
import type { MoodRecord, PeriodRecord } from '@/domain/health/journal'
import Dexie, { type Table } from 'dexie'

import { parseIsoInstant } from '@/domain/health/time'
import type { AppSettings, MetricRecord, SleepRecord, WorkoutRecord } from '@/domain/health/types'

export interface SettingsRow {
  key: 'app'
  value: AppSettings
}

const DEFAULT_SETTINGS: AppSettings = {
  displayWeightUnit: 'kg',
  schemaVersion: 1,
}

function normalizeInstant(value: string): string {
  return parseIsoInstant(value).toISOString()
}

export function normalizeMetricRecord(record: MetricRecord): MetricRecord {
  return {
    ...record,
    recordedAt: normalizeInstant(record.recordedAt),
    createdAt: normalizeInstant(record.createdAt),
    updatedAt: normalizeInstant(record.updatedAt),
  }
}

export function normalizeSleepRecord(record: SleepRecord): SleepRecord {
  return {
    ...record,
    startAt: normalizeInstant(record.startAt),
    endAt: normalizeInstant(record.endAt),
    createdAt: normalizeInstant(record.createdAt),
    updatedAt: normalizeInstant(record.updatedAt),
  }
}

export function normalizeWorkoutRecord(record: WorkoutRecord): WorkoutRecord {
  return {
    ...record,
    startedAt: normalizeInstant(record.startedAt),
    createdAt: normalizeInstant(record.createdAt),
    updatedAt: normalizeInstant(record.updatedAt),
  }
}

export class CareBabyDb extends Dexie {
  workoutOverrides!: Table<WorkoutOverride, string>
  intakes!: Table<IntakeRecord, string>
  savedFoods!: Table<SavedFood, string>
  moods!: Table<MoodRecord, string>
  periods!: Table<PeriodRecord, string>
  metrics!: Table<MetricRecord, string>
  sleeps!: Table<SleepRecord, string>
  workouts!: Table<WorkoutRecord, string>
  settings!: Table<SettingsRow, string>

  constructor(databaseName = 'carebaby') {
    super(databaseName)

    this.version(1).stores({
      metrics: 'id,type,recordedAt',
      sleeps: 'id,startAt,endAt',
      workouts: 'id,startedAt',
    })

    this.version(2)
      .stores({
        metrics: 'id,type,recordedAt,localDay,source,[source+externalId]',
        sleeps: 'id,startAt,endAt,wakeLocalDay,source,[source+externalId]',
        workouts: 'id,startedAt,localDay,activityType,source,[source+externalId]',
        settings: 'key',
      })
      .upgrade(async (transaction) => {
        const metrics = transaction.table<MetricRecord, string>('metrics')
        const sleeps = transaction.table<SleepRecord, string>('sleeps')
        const workouts = transaction.table<WorkoutRecord, string>('workouts')
        const settings = transaction.table<SettingsRow, string>('settings')
        await metrics.toCollection().modify((record) => {
          Object.assign(record, normalizeMetricRecord(record))
        })
        await sleeps.toCollection().modify((record) => {
          Object.assign(record, normalizeSleepRecord(record))
        })
        await workouts.toCollection().modify((record) => {
          Object.assign(record, normalizeWorkoutRecord(record))
        })
        if ((await settings.get('app')) === undefined) {
          await settings.put({ key: 'app', value: { ...DEFAULT_SETTINGS } })
        }
      })

    this.version(3).stores({ moods: 'localDay', periods: 'localDay' })
    this.version(4).stores({ intakes: 'id,localDay', savedFoods: 'id' })
    this.version(5).stores({ workoutOverrides: 'key' })

    this.on('populate', (transaction) =>
      transaction
        .table<SettingsRow, string>('settings')
        .put({ key: 'app', value: { ...DEFAULT_SETTINGS } }),
    )
  }
}
